import {
  Alert,
  Button,
  DatePicker,
  Pagination,
  Select,
  Table,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import PageTitle from 'components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import {
  DANCE_KIND_COLOR,
  DANCE_KIND_LABEL,
  DANCE_REASON_LABEL,
  DANCE_SESSION_STATUS_LABEL,
  DANCE_SESSION_STATUS_OPTIONS,
  labelOf,
} from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import {
  DanceScoreRow,
  DanceSessionStatus,
  DanceSessionView,
  ScoreTier,
} from 'pages/star-shared/types';
import {
  TABLE_EMPTY,
  formatDate,
  formatDateTime,
  formatNumber,
  personName,
} from 'pages/star-shared/utils';
import { usePagedList } from 'pages/star-shared/use-paged-list';
import { useStarRules } from 'pages/star-shared/use-star-rules';
import StarAmount from 'pages/star-shared/components/star-amount';
import { Ellipsis, NoWrap, UserCell } from 'pages/star-shared/components/status-tags';
import UserPicker, { UserPickerValue } from 'pages/star-shared/components/user-picker';
import { PickedValue, StarAccountSelect } from 'pages/star-shared/components/remote-search-select';
import NoteActionModal, { NoteActionConfig } from 'pages/star-shared/components/note-action-modal';
import { colors } from 'theme/tokens';

const LIMIT = 20;
type RangeValue = [Dayjs | null, Dayjs | null] | null;

interface Filter {
  from?: string;
  to?: string;
  teacherId?: number;
  learnerId?: number;
  status?: DanceSessionStatus;
  page: number;
  limit: number;
}

const tiersText = (tiers: ScoreTier[]) =>
  tiers.map((t) => `≥ ${formatNumber(t.score)} → ${formatNumber(t.star)} ⭐`).join(' · ');

// Tên học viên chưa thu hồi được Sao (notReversed chỉ có userId).
const learnerName = (s: DanceSessionView, userId: number) => {
  const user = s.scores?.find((x) => x.userId === userId)?.user;
  return user ? personName(user) : `#${userId}`;
};

const scoreColumns: ColumnsType<DanceScoreRow> = [
  {
    title: 'Học viên',
    key: 'user',
    width: 240,
    render: (_, r) => <UserCell user={r.user || { id: r.userId }} />,
  },
  {
    title: 'Có mặt',
    key: 'present',
    width: 90,
    render: (_, r) => (r.present ? 'Có' : <span style={{ color: colors.dangerInk }}>Vắng</span>),
  },
  {
    title: 'Điểm',
    key: 'score',
    width: 80,
    align: 'right',
    render: (_, r) => (r.score === null || r.score === undefined ? '—' : formatNumber(r.score)),
  },
  {
    title: 'Sao',
    key: 'starAwarded',
    width: 90,
    align: 'right',
    render: (_, r) => <StarAmount value={r.starAwarded ?? 0} strong={r.starAwarded > 0} />,
  },
  {
    title: 'Lý do không có Sao',
    key: 'reason',
    render: (_, r) =>
      r.reason ? (
        <Tag color={r.reason === 'CANCELLED' ? 'default' : 'gold'}>
          {labelOf(DANCE_REASON_LABEL, r.reason)}
        </Tag>
      ) : (
        '—'
      ),
  },
];

// Chấm điểm nhảy — Admin giám sát buổi giáo viên chấm trên app (SPEC-NHIEM-VU-MO-RONG.md §4).
const StarDancePage = () => {
  const { dance: rules, fromServer } = useStarRules();
  const [filter, setFilter] = useState<Filter>({ page: 1, limit: LIMIT });
  const [range, setRange] = useState<RangeValue>(null);
  const [teacher, setTeacher] = useState<UserPickerValue>(undefined);
  const [learner, setLearner] = useState<PickedValue>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const [action, setAction] = useState<NoteActionConfig | null>(null);
  const { items, total, loading, setItems } = usePagedList<DanceSessionView>(
    API.starsDanceSessions,
    filter,
    { refreshKey }
  );

  const patchFilter = (patch: Partial<Filter>) =>
    setFilter((prev) => ({ ...prev, ...patch, page: 1 }));

  const openCancel = (s: DanceSessionView) => {
    const stars = s.totals?.stars ?? 0;
    setAction({
      title: 'Huỷ buổi chấm điểm',
      description: (
        <div>
          <div>
            {formatDate(s.sessionDate)} · {labelOf(DANCE_KIND_LABEL, s.kind)} ·{' '}
            <strong>{s.title}</strong> · giáo viên {personName(s.teacher)}
          </div>
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 8 }}
            message={
              <span>
                Sao của buổi (<StarAmount value={stars} />) sẽ bị <strong>thu hồi</strong> khỏi ví
                từng học viên. Ai không đủ Sao thì giữ phần thiếu và được ghi vào danh sách "chưa
                thu hồi". Buổi khác từng bị trần chặn không tự được trả lại Sao.
              </span>
            }
          />
        </div>
      ),
      noteRequired: true,
      noteLabel: 'Lý do huỷ buổi',
      confirmText: 'Huỷ buổi',
      danger: true,
      onSubmit: async (note) => {
        try {
          const res: any = await AxiosUtils.patch(`${API.starsDanceSessions}/${s.id}/cancel`, {
            reason: note,
          });
          const next: DanceSessionView | null =
            res?.data && typeof res.data === 'object' ? res.data : null;
          const notReversed = next?.notReversed || [];
          if (notReversed.length) {
            notification.warning({
              message: `Đã huỷ buổi "${s.title}", còn ${notReversed.length} người chưa thu hồi được Sao.`,
              description: notReversed
                .map((n) => `${learnerName(next || s, n.userId)}: ${formatNumber(n.star)} ⭐`)
                .join('; '),
              duration: 10,
            });
          } else {
            notification.success({ message: `Đã huỷ buổi "${s.title}" và thu hồi Sao.` });
          }
          if (next?.id) setItems((prev) => prev.map((x) => (x.id === next.id ? next : x)));
          else setRefreshKey((k) => k + 1);
        } catch (err) {
          notification.error({ message: mapStarError(err) });
          throw err;
        }
      },
    });
  };

  const columns: ColumnsType<DanceSessionView> = [
    {
      title: 'Ngày',
      key: 'sessionDate',
      width: 100,
      render: (_, s) => <NoWrap>{formatDate(s.sessionDate)}</NoWrap>,
    },
    {
      title: 'Loại',
      key: 'kind',
      width: 110,
      render: (_, s) => (
        <Tag color={DANCE_KIND_COLOR[s.kind]}>{labelOf(DANCE_KIND_LABEL, s.kind)}</Tag>
      ),
    },
    {
      title: 'Tên bài',
      key: 'title',
      width: 180,
      render: (_, s) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{s.title}</div>
          {s.note && <Ellipsis value={s.note} width={170} />}
        </div>
      ),
    },
    {
      title: 'Giáo viên',
      key: 'teacher',
      width: 160,
      render: (_, s) => <UserCell user={s.teacher} />,
    },
    {
      title: 'Có mặt',
      key: 'present',
      width: 86,
      align: 'right',
      render: (_, s) =>
        s.totals ? (
          <NoWrap>
            {formatNumber(s.totals.present)}/{formatNumber(s.totals.learners)}
          </NoWrap>
        ) : (
          '—'
        ),
    },
    {
      title: 'Tổng Sao',
      key: 'stars',
      width: 100,
      align: 'right',
      render: (_, s) => <StarAmount value={s.totals?.stars ?? 0} />,
    },
    {
      title: 'Lần sửa',
      key: 'editCount',
      width: 96,
      align: 'right',
      render: (_, s) => (
        <Tooltip
          title={
            s.editableUntil
              ? `Giáo viên còn sửa được tới ${formatDateTime(s.editableUntil)}`
              : 'Đã hết hạn sửa'
          }
        >
          <span style={{ color: s.editCount > 0 ? colors.dangerInk : undefined, fontWeight: 600 }}>
            {formatNumber(s.editCount ?? 0)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 150,
      render: (_, s) =>
        s.status === 'CANCELLED' ? (
          <div>
            <Tag>{labelOf(DANCE_SESSION_STATUS_LABEL, s.status)}</Tag>
            <div style={{ color: colors.textSecondary, fontSize: 12 }}>
              {personName(s.cancelledBy)} · {formatDateTime(s.cancelledAt)}
            </div>
            {s.cancelReason && <Ellipsis value={s.cancelReason} width={140} />}
          </div>
        ) : (
          <Tag color="green">{labelOf(DANCE_SESSION_STATUS_LABEL, s.status)}</Tag>
        ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 90,
      render: (_, s) => (
        <Tooltip title={s.status === 'ACTIVE' ? 'Huỷ buổi (thu hồi Sao)' : 'Buổi đã huỷ'}>
          <Button
            type="text"
            danger
            icon={<StopOutlined />}
            aria-label="Huỷ buổi"
            disabled={s.status !== 'ACTIVE'}
            onClick={() => openCancel(s)}
          />
        </Tooltip>
      ),
    },
  ];

  const expandedRow = (s: DanceSessionView) => (
    <div style={{ maxWidth: 900 }}>
      {!!s.notReversed?.length && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message={`Chưa thu hồi được (ví không đủ Sao): ${s.notReversed
            .map((n) => `${learnerName(s, n.userId)} ${formatNumber(n.star)} ⭐`)
            .join('; ')}`}
        />
      )}
      <Table
        size="small"
        columns={scoreColumns}
        rowKey={(r) => r.id ?? r.userId}
        pagination={false}
        dataSource={s.scores || []}
        locale={{ emptyText: 'Buổi chưa có điểm.' }}
      />
      <div style={{ color: colors.textSecondary, fontSize: 12, marginTop: 6 }}>
        Chấm lúc {formatDateTime(s.createdAt)}
        {s.editCount > 0 ? ` · sửa lần cuối ${formatDateTime(s.updatedAt)}` : ''}
      </div>
    </div>
  );

  return (
    <div>
      <PageTitle title="Chấm điểm nhảy" />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <div>
            <div style={{ marginBottom: 4 }}>
              Giáo viên nhảy chấm trên app (<em>Điểm Sao → Chấm điểm nhảy</em>); Sao vào ví học viên
              ngay. Trang này để giám sát mọi buổi, điểm, lần sửa, lần huỷ.
            </div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>
                <strong>Buổi tập</strong>: {tiersText(rules.practiceTiers)}; tối đa{' '}
                {rules.practiceMaxPerWeek} buổi có Sao/tuần/người, 1 buổi/ngày.
              </li>
              <li>
                <strong>Kiểm tra bài mới</strong>: {tiersText(rules.examTiers)}; tối đa{' '}
                {rules.examMaxPerMonth} bài có Sao/tháng/người.
              </li>
              <li>
                Thang 10 điểm, bước 0,5. Vắng → 0; dưới điểm sàn → ghi điểm, không có Sao. Chấm lùi
                tối đa {rules.backdateDays} ngày; giáo viên sửa được trong {rules.editHours} giờ,
                sau đó chỉ Admin/SMOD <strong>huỷ buổi</strong> (Sao của buổi bị thu hồi).
              </li>
            </ul>
            {!fromServer && (
              <div style={{ color: colors.textSecondary, fontSize: 12, marginTop: 4 }}>
                Số liệu trên là mặc định; Admin chỉnh ở Điểm Sao → Cấu hình.
              </div>
            )}
          </div>
        }
      />
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <DatePicker.RangePicker
          format="DD/MM/YYYY"
          style={{ width: 260 }}
          placeholder={['Từ ngày', 'Đến ngày']}
          allowEmpty={[true, true]}
          value={range}
          onChange={(value) => {
            setRange(value);
            patchFilter({
              from: value?.[0] ? dayjs(value[0]).format('YYYY-MM-DD') : undefined,
              to: value?.[1] ? dayjs(value[1]).format('YYYY-MM-DD') : undefined,
            });
          }}
        />
        <UserPicker
          placeholder="Giáo viên (gõ ≥ 2 chữ)"
          style={{ width: 230 }}
          value={teacher}
          onChange={(value) => {
            setTeacher(value);
            patchFilter({ teacherId: value?.value });
          }}
        />
        <StarAccountSelect
          group="CREATOR"
          placeholder="Học viên (Creator Dance)"
          allowClear
          style={{ width: 230 }}
          value={learner}
          onChange={(value) => {
            setLearner(value);
            patchFilter({ learnerId: value?.value });
          }}
        />
        <Select
          allowClear
          placeholder="Mọi trạng thái"
          style={{ width: 160 }}
          value={filter.status}
          options={DANCE_SESSION_STATUS_OPTIONS}
          onChange={(status) => patchFilter({ status })}
        />
      </div>
      <div className="custom-table">
        <Table
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={items}
          loading={loading}
          locale={{ emptyText: TABLE_EMPTY.emptyText }}
          scroll={{ x: 1120 }}
          expandable={{ expandedRowRender: expandedRow }}
        />
        {total > LIMIT && (
          <Pagination
            showSizeChanger={false}
            current={filter.page}
            onChange={(page) => setFilter((prev) => ({ ...prev, page }))}
            showLessItems
            total={total}
            pageSize={LIMIT}
          />
        )}
      </div>
      <NoteActionModal config={action} onClose={() => setAction(null)} />
    </div>
  );
};

export default StarDancePage;
